import type { AuthenticatedUser, ResourceUser } from '@src/types';
import { isoWeekday } from '@src/utils';

export function userResourceId(userId: number): string {
  return `user-${userId}`;
}

type BusinessHours = {
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
};
type Resource = {
  id: string;
  title: string;
  businessHours: BusinessHours[];
  manager: number | null;
  order: number;
  userId: number;
};
type ResourceNode = Resource & {
  [key: string]: ResourceNode[];
};

function getBuisnessHoursForResrouce(resource: ResourceUser): BusinessHours[] {
  let buisinessHours: BusinessHours[] = [];
  if (!resource.worktime || !resource.worktime.length) {
    return buisinessHours;
  }
  const toMinutes = (t: string) => {
    if (!t) {
      return null;
    }
    const [hh, mm] = t.split(':').map(Number);
    return hh * 60 + mm;
  };

  const fromMinutes = (m: number) => {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${h}:${min.toString().padStart(2, '0')}`;
  };

  const intervals: Array<{ start: number; end: number; day: number }> = [];
  for (const row of resource.worktime || []) {
    const day = row.weekDay;

    const ms = toMinutes(row.starTimeMorning);
    const me = toMinutes(row.endTimeMorning);
    if (ms != null && me != null && me > ms) {
      intervals.push({ start: ms, end: me, day });
    }

    const as = toMinutes(row.startTimeAfternoon);
    const ae = toMinutes(row.endTimeAfternoon);
    if (as != null && ae != null && ae > as) {
      intervals.push({ start: as, end: ae, day });
    }
  }

  const map: Map<string, { start: number; end: number; days: Set<number> }> =
    new Map();
  for (const it of intervals) {
    const key = `${it.start}-${it.end}`;
    if (!map.has(key)) {
      map.set(key, { start: it.start, end: it.end, days: new Set() });
    }
    const entry = map.get(key);
    if (entry) entry.days.add(it.day);
  }

  buisinessHours = Array.from(map.values()).map(({ start, end, days }) => ({
    startTime: fromMinutes(start),
    endTime: fromMinutes(end),
    daysOfWeek: Array.from(days).sort((a: number, b: number) => a - b),
  }));

  buisinessHours.sort((a, b) => {
    const sa = a.startTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    const sb = b.startTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    if (sa !== sb) {
      return sa - sb;
    }
    const ea = a.endTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    const eb = b.endTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    return ea - eb;
  });
  return buisinessHours;
}

function buildResourcesHierarchy(
  users: Resource[],
  {
    idKey = 'userId',
    managerKey = 'manager',
    childrenKey = 'children',
  }: {
    idKey?: keyof Resource;
    managerKey?: keyof Resource;
    childrenKey?: string;
  } = {},
) {
  const nodeById = new Map<Resource[keyof Resource], ResourceNode>();
  for (const u of users) {
    const clone: ResourceNode = { ...u, [childrenKey]: [] } as ResourceNode;
    nodeById.set(clone[idKey], clone);
  }

  const roots: ResourceNode[] = [];

  for (const u of users) {
    const node = nodeById.get(u[idKey]);
    const managerId = u[managerKey];

    if (!node) {
      continue;
    }

    if (managerId == null || !nodeById.has(managerId)) {
      // No manager or manager missing -> root
      roots.push(node);
    } else {
      const managerNode = nodeById.get(managerId);
      if (managerNode && node) {
        managerNode[childrenKey].push(node);
      }
    }
  }

  return roots;
}

export function usersToCalendarResources(
  users: ResourceUser[],
  currentUser: AuthenticatedUser | null,
) {
  if (!users?.length) {
    return [];
  }
  const tree = buildResourcesHierarchy(
    users.map((item: ResourceUser): Resource => {
      const idStr = `user-${String(item.id)}`;
      const title = (item: ResourceUser) => {
        if (item.familyName && item.firstName) {
          return `${item.familyName}, ${item.firstName}`;
        } else {
          return item.name;
        }
      };
      const resource: Resource = {
        id: idStr,
        title: title(item),
        businessHours: getBuisnessHoursForResrouce(item),
        manager: item.managerId,
        order: 2,
        userId: item.id,
      };
      if (item.id === currentUser?.id) {
        resource.manager = null;
        resource.order = 0;
      }
      if (item.id === currentUser?.managerId) {
        resource.order = 1;
      }
      return resource;
    }),
  );
  return tree;
}

export function getUserWorktimeForDate(
  userId: number,
  users: ResourceUser[],
  date: string,
) {
  if (!userId || !users || !users.length || !date) {
    return null;
  }

  const user = users.find((u) => u.id === userId);
  if (!user || !user.worktime || !user.worktime.length) {
    return null;
  }

  const dayNumber = isoWeekday(date);
  const dayWorkingTime = user.worktime.find((w) => w.weekDay === dayNumber);
  if (!dayWorkingTime) {
    return null;
  }
  const startTime = dayWorkingTime.starTimeMorning;
  const endTime = dayWorkingTime.endTimeAfternoon;
  return { start: startTime, end: endTime };
}
